import React from "react";
import {autobind} from "core-decorators";
import {computed} from "mobx";
import {inject, observer} from "mobx-react";
import {Table, Tooltip} from "antd";
import {type SortOrder} from "antd/lib/table/interface";
import {FolderOutlined, InfoCircleOutlined} from "@ant-design/icons";
import _isString from "lodash/isString";
import _compact from "lodash/compact";
import _uniq from "lodash/uniq";

import "./TablePieces.scss";
import {paths} from "@/routes";
import {PiecesQuery} from "@/graphql/types";
import Store from "@/stores";
import TablePiecesActions from "./TablePiecesActions/TablePiecesActions";

type Pieces = PiecesQuery["pieces"];
type Piece = Pieces[0];

export interface ITablePiecesProps {
  pieces?: Pieces;
  store?: Store;
}

function isFolder(piece: Piece): boolean {
  return _isString(piece.id);
}

@inject("store")
@autobind
@observer
export default class TablePieces extends React.Component<ITablePiecesProps> {
  @computed({keepAlive: true})
  get folders() {
    const pieces = this.props.pieces || [];
    return _uniq(_compact(pieces.map(({folder}) => folder)));
  }

  onCellColSpanIfFolder(piece: Piece) {
    return {colSpan: isFolder(piece) ? 6 : 1};
  }

  onCellRemoveIfFolder(piece: Piece) {
    return {colSpan: isFolder(piece) ? 0 : 1};
  }

  // Add "fake pieces" that represent a folder so that the table renders the folders
  @computed({keepAlive: true})
  get piecesAndFolders(): Piece[] | undefined {
    return [...(this.props.pieces || []), ...this.folders.map((folder) => ({id: folder, folder} as unknown as Piece))];
  }

  sorterComposer(a: Piece, b: Piece, sortOrder: SortOrder | undefined) {
    return this.sorterString(a, b, sortOrder, "composer");
  }

  sorterCreatedAt(a: Piece, b: Piece, sortOrder: SortOrder | undefined) {
    const direction = sortOrder === "ascend" ? 1 : -1;
    if (a.folder === b.folder) {
      if (isFolder(a)) return -1 * direction;
      if (isFolder(b)) return 1 * direction;
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    } else if (!a.folder) return -1 * direction;
    else if (!b.folder) return 1 * direction;
    else return a.folder.localeCompare(b.folder) * direction;
  }

  sorterLanguage(a: Piece, b: Piece, sortOrder: SortOrder | undefined) {
    return this.sorterString(a, b, sortOrder, "language");
  }

  sorterStatus(a: Piece, b: Piece, sortOrder: SortOrder | undefined) {
    return this.sorterString(a, b, sortOrder, "status");
  }

  // A sorter that can be used for any string property of the Piece type
  sorterString<K extends keyof Piece>(
    a: Piece,
    b: Piece,
    sortOrder: SortOrder | undefined,
    property: K extends string ? (Piece[K] extends string ? K : never) : never,
  ) {
    const direction = sortOrder === "ascend" ? 1 : -1;
    if (a.folder === b.folder) {
      if (isFolder(a)) return -1 * direction;
      if (isFolder(b)) return 1 * direction;
      return (a[property] as string).localeCompare(b[property] as string);
    } else if (!a.folder) return -1 * direction;
    else if (!b.folder) return 1 * direction;
    else return a.folder.localeCompare(b.folder) * direction;
  }

  sorterTitle(a: Piece, b: Piece, sortOrder: SortOrder | undefined) {
    return this.sorterString(a, b, sortOrder, "title");
  }

  renderCreatedAtColumn(text: any, record: Piece, index: number): React.ReactNode {
    return new Date(text).toLocaleString();
  }

  renderErrorText(error?: string | null): React.ReactNode {
    return (
      <>
        {error && (
          <>
            {error}
            <br />
            <br />
          </>
        )}
        No credits have been consumed.
        <br />
        <br />
        Please ensure your score follows the{" "}
        <a href={paths.scoreGuidelines()} target="_blank" rel="help noreferrer">
          score preparation guidelines
        </a>
        .
        <br />
        <br />
        Need help?{" "}
        <a href="/help" target="_blank" rel="help noreferrer">
          Click here
        </a>
        .
      </>
    );
  }

  renderFolder(piece: Piece): React.ReactNode {
    return (
      <span style={{display: "flex", alignItems: "center"}}>
        <FolderOutlined style={{marginRight: 5, fontSize: 22}} />
        {piece.folder}
      </span>
    );
  }

  renderLanguageColumn(text: any, record: Piece, index: number): React.ReactNode {
    return text?.charAt(0)?.toUpperCase() + text?.slice(1); // Upper case for the first letter
  }

  renderStatusColumn(status: string, piece: Piece, index: number): React.ReactNode {
    return (
      <span style={{whiteSpace: "nowrap"}}>
        {status}
        {status === "error" && (
          <Tooltip
            title={this.renderErrorText(piece.error)}
            overlayStyle={{whiteSpace: "pre-line"}}
            placement="right"
            arrowPointAtCenter
          >
            <InfoCircleOutlined style={{marginLeft: 5}} />
          </Tooltip>
        )}
      </span>
    );
  }

  public render() {
    const {pieces, store} = this.props;
    const {session} = store!;
    // observe piece attributes so we re-render when they are changed in an action
    const piecesAttrs = pieces?.map((p) => [p.folder, p.title, p.composer, p.created_at, p.status]); // eslint-disable-line
    return (
      <>
        <Table<Piece>
          className="TablePieces"
          // we need to create a new array to trigger a re-render of the table component (for deleted elements)
          dataSource={this.piecesAndFolders?.slice()}
          rowKey="id"
          rowClassName={(piece) => (isFolder(piece) ? "folder-row" : "")}
          loading={!pieces}
          pagination={false}
          scroll={{x: true}}
        >
          <Table.Column<Piece>
            title="Actions"
            key="actions"
            render={(_, piece) =>
              isFolder(piece) ? (
                this.renderFolder(piece)
              ) : (
                <TablePiecesActions piece={piece} pieces={this.props.pieces!} />
              )
            }
            onCell={this.onCellColSpanIfFolder}
          />
          <Table.Column<Piece>
            title="Title"
            dataIndex="title"
            key="title"
            // Use a new lambda in render so the cell gets updated when it changes
            render={(title) => title}
            onCell={this.onCellRemoveIfFolder}
            sorter={this.sorterTitle}
            sortDirections={["ascend", "descend", "ascend"]}
            defaultSortOrder="ascend"
          />
          <Table.Column<Piece>
            title="Composer"
            dataIndex="composer"
            key="composer"
            // Use a new lambda in render so the cell gets updated when it changes
            render={(composer) => composer}
            onCell={this.onCellRemoveIfFolder}
            sorter={this.sorterComposer}
            sortDirections={["ascend", "descend", "ascend"]}
          />
          <Table.Column<Piece>
            title="Language"
            dataIndex="language"
            key="language"
            render={this.renderLanguageColumn}
            onCell={this.onCellRemoveIfFolder}
            sorter={this.sorterLanguage}
            sortDirections={["ascend", "descend", "ascend"]}
          />
          <Table.Column<Piece>
            title="Created at"
            dataIndex="created_at"
            key="created_at"
            render={this.renderCreatedAtColumn}
            onCell={this.onCellRemoveIfFolder}
            sorter={this.sorterCreatedAt}
            sortDirections={["descend", "ascend", "descend"]}
          />
          {session.isEnsembleConductor && (
            <Table.Column<Piece>
              title="Status"
              dataIndex="status"
              key="status"
              className="status" // for tests
              // Use a new lambda in render so the cell gets updated when it changes
              render={(...args) => this.renderStatusColumn(...args)}
              onCell={this.onCellRemoveIfFolder}
              sorter={this.sorterStatus}
              sortDirections={["ascend", "descend", "ascend"]}
            />
          )}
        </Table>
      </>
    );
  }
}
